<template>
    <PageHeader :crumbs="[{ label: $t('portal.customers.title') }]" />

    <div class="tw-p-2">
        <div class="tw-h-full tw-w-full tw-p-4 tw-bg-white tw-rounded tw-border tw-border-b-2 tw-min-h-[calc(100dvh-125px)]">
            <div class="tw-justify-between tw-flex tw-items-center tw-gap-2">
                <FormField v-model="search" input-dir="ltr" :placeholder="$t('portal.customers.search')"
                    left-icon="clarity:search-line" type="text" class="tw-min-w-[100px]" hide-errors @keyup.enter="fetch(1)" />
                <a-button left-icon="heroicons:plus-16-solid" @click="openCreate">{{ $t('buttons.create') }}</a-button>
            </div>

            <div class="tw-mt-5">
                <DataTable :columns="columns" :loading="loading" :count="items.length">
                    <tr v-for="c in items" :key="c.id + '-' + c.updated_at" :class="[rowClass, 'tw-cursor-pointer']" @click="goto(c)">
                        <td :class="cellClass"><span class="tw-font-medium tw-text-gray-800">{{ c.name }}</span></td>
                        <td :class="cellClass"><span class="tw-text-gray-500">{{ c.email || '—' }}</span></td>
                        <td :class="cellClass" @click.stop>
                            <div v-if="c.phone" class="tw-flex tw-items-center tw-gap-2" dir="ltr">
                                <span class="tw-text-gray-500">{{ c.phone }}</span>
                                <a :href="waLink(c.phone)" target="_blank" rel="noopener"
                                    :title="$t('buttons.whatsapp')"
                                    class="tw-inline-flex tw-items-center tw-justify-center tw-h-7 tw-w-7 tw-rounded-lg tw-bg-emerald-50 tw-text-emerald-600 tw-border tw-border-solid tw-border-emerald-200 tw-duration-200 hover:tw-bg-emerald-500 hover:tw-text-white">
                                    <icon icon="mdi:whatsapp" class="tw-text-base" />
                                </a>
                            </div>
                            <span v-else class="tw-text-gray-400">—</span>
                        </td>
                        <td :class="cellClass"><span class="tw-text-gray-500">{{ c.country || '—' }}</span></td>
                        <td :class="cellClass"><span class="tw-text-gray-500 tw-block tw-max-w-[200px] tw-truncate" :title="c.note || ''">{{ c.note || '—' }}</span></td>
                        <td :class="cellClass" @click.stop>
                            <select :value="c.payment_status || 'pending'" @change="changePayment(c, $event.target.value)"
                                :disabled="savingId === c.id"
                                class="tw-text-xs tw-font-semibold tw-rounded-full tw-border tw-border-solid tw-px-2 tw-py-1 tw-cursor-pointer tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-200"
                                :class="paymentClass(c.payment_status)">
                                <option value="paid">{{ $t('portal.payment.paid') }}</option>
                                <option value="pending">{{ $t('portal.payment.pending') }}</option>
                                <option value="refunded">{{ $t('portal.payment.refunded') }}</option>
                            </select>
                        </td>
                        <td :class="cellClass"><span class="tw-text-gray-600">{{ c.purchases_count ?? 0 }}</span></td>
                        <td :class="cellClass">
                            <StatusBadge :status="c.status" :label="c.status === 'disabled' ? $t('portal.fields.disabled') : $t('portal.fields.enabled')" />
                        </td>
                        <td :class="cellClass" @click.stop>
                            <div class="tw-flex tw-items-center tw-gap-2 tw-justify-end">
                                <a-action-button icon="solar:eye-bold-duotone" intent="dark" @click="goto(c)" />
                                <a-action-button icon="solar:pen-2-bold-duotone" intent="secondary" @click="openEdit(c)" />
                                <a-action-button icon="solar:trash-bin-2-bold-duotone" intent="danger" @click="askDelete(c)" />
                            </div>
                        </td>
                    </tr>
                </DataTable>
            </div>

            <div class="tw-my-4"></div>

            <TablePagination v-if="items.length" :has-next="options.current_page < options.last_page"
                :has-previous="options.current_page > 1" @next="fetch(options.current_page + 1)"
                @previous="fetch(options.current_page - 1)" />
        </div>
    </div>

    <CustomerFormModal :visible="formVisible" :customer="editing" @cancel="formVisible = false" @saved="onSaved" />
    <DeleteModal :visible="deleteVisible" :loading="deleting" @cancel="deleteVisible = false" @confirm="confirmDelete" />
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { paginate, remove, update } from '@/api/customers';
import PageHeader from '@/components/PageHeader.vue';
import StatusBadge from '@/components/StatusBadge.vue';
import FormField from '@/components/form/FormField';
import DataTable from '@/components/table/DataTable.vue';
import TablePagination from '@/components/table/TablePagination.vue';
import DeleteModal from '@/components/modals/DeleteModal.vue';
import CustomerFormModal from './CustomerFormModal.vue';
import { rowClass, cellClass } from '@/composables/useTableStyles';
import useAlert from '@/composables/useAlert';

const router = useRouter();
const i18n = useI18n();

const columns = computed(() => [
    { label: i18n.t('fields.name') },
    { label: i18n.t('fields.email') },
    { label: i18n.t('fields.customer_phone'), classes: ['tw-w-[140px]'] },
    { label: i18n.t('portal.fields.country'), classes: ['tw-w-[120px]'] },
    { label: i18n.t('fields.customer_notes') },
    { label: i18n.t('portal.fields.payment'), classes: ['tw-w-[130px]'] },
    { label: i18n.t('portal.fields.products'), classes: ['tw-w-[90px]'] },
    { label: i18n.t('portal.fields.portal'), classes: ['tw-w-[120px]'] },
    { label: i18n.t('fields.actions'), classes: ['tw-w-[140px]', 'tw-text-end'] },
]);

const items = ref([]);
const options = ref({ current_page: 1, last_page: 1 });
const loading = ref(false);
const search = ref('');

const savingId = ref(null);

const waLink = (phone) => {
    const digits = String(phone || '').replace(/[^\d]/g, '');
    return `https://wa.me/${digits}`;
};

const paymentClass = (status) => ({
    paid: 'tw-bg-emerald-50 tw-text-emerald-700 tw-border-emerald-200',
    pending: 'tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200',
    refunded: 'tw-bg-rose-50 tw-text-rose-700 tw-border-rose-200',
}[status] || 'tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200');

const changePayment = async (customer, value) => {
    if (value === customer.payment_status) return;
    savingId.value = customer.id;
    const previous = customer.payment_status;
    customer.payment_status = value; // optimistic
    try {
        await update(customer.id, { payment_status: value });
        useAlert(i18n.t('messages.updated-successfully', { field: i18n.t('portal.entities.customer') }));
    } catch (e) {
        customer.payment_status = previous; // revert on failure
    } finally {
        savingId.value = null;
    }
};

const formVisible = ref(false);
const editing = ref(null);
const deleteVisible = ref(false);
const deleting = ref(false);
const toDelete = ref(null);

async function fetch(page = 1) {
    loading.value = true;
    try {
        const { data } = await paginate({ per_page: 10, page, search: search.value });
        const { data: rows, ...ops } = data.data;
        items.value = rows;
        options.value = ops;
    } catch (e) { /* interceptor */ } finally {
        loading.value = false;
    }
}

const goto = (c) => router.push({ name: 'admin.customers.show', params: { id: c.id } });
const openCreate = () => { editing.value = null; formVisible.value = true; };
const openEdit = (c) => { editing.value = { ...c }; formVisible.value = true; };
const onSaved = () => { formVisible.value = false; fetch(options.value.current_page); };

const askDelete = (c) => { toDelete.value = c; deleteVisible.value = true; };
const confirmDelete = async () => {
    deleting.value = true;
    try {
        await remove(toDelete.value.id);
        useAlert(i18n.t('messages.deleted-successfully', { field: i18n.t('portal.entities.customer') }));
        deleteVisible.value = false;
        fetch(options.value.current_page);
    } catch (e) { /* interceptor */ } finally {
        deleting.value = false;
    }
};

fetch();
</script>
